<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const emit = defineEmits(['click', 'mounted', 'loaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  source: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['fill', 'line', 'symbol', 'circle', 'heatmap', 'fill-extrusion', 'raster', 'hillshade', 'background', 'sky', 'slot'].includes(value),
  },
  options: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  beforeId: {
    type: String,
    required: false,
    default: null,
  },
  slot: {
    type: String,
    required: false,
    default: null,
  },
  groupId: {
    type: String,
    required: false,
    default: null,
  },
})

// Throttle function to limit cursor updates
const throttle = (func, limit) => {
  let inThrottle
  return (...args) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

const throttledCursorUpdate = throttle((cursor) => {
  props.map.getCanvas().style.cursor = cursor
}, 50)

const addLayerListeners = (layer) => {
  props.map.on('mousemove', layer, (e) => {
    throttledCursorUpdate('pointer')
  })

  props.map.on('mouseleave', layer, () => {
    throttledCursorUpdate('default')
  })

  props.map.on('click', layer, (e) => {
    emit('click', e)
  })
}

const removeLayerListeners = (layer) => {
  props.map.off('mousemove', layer)
  props.map.off('mouseleave', layer)
  props.map.off('click', layer)
}

const addLayer = async () => {
  if (!props.map.getLayer(props.id)) {
    const layerOptions = {
      id: props.id,
      type: props.type,
      ...props.options,
      minzoom: 0,
      maxzoom: 24,
    }

    if (props.slot) {
      layerOptions.slot = props.slot
    }

    if (props.source) {
      layerOptions.source = props.source
    }

    if (props.groupId) {
      layerOptions.metadata = {
        groupId: props.groupId,
      }
    }

    if (props.beforeId && props.map.getLayer(props.beforeId)) {
      props.map.addLayer(layerOptions, props.beforeId)
    } else {
      props.map.addLayer(layerOptions)
    }

    // Only add listeners for interactive layers
    if (props.type !== 'slot' && props.type !== 'background' && props.type !== 'sky') {
      addLayerListeners(props.id)
    }


    const waitForSourceLoaded = (map, sourceId, interval = 100) => {
      const checkSource = setInterval(() => {
        if (!map.getSource(sourceId)) {
          clearInterval(checkSource)
          return
        }

        if (map.isSourceLoaded(sourceId)) {
          clearInterval(checkSource)
          emit('loaded')
        }
      }, interval)
    }

    waitForSourceLoaded(props.map, props.source)
  } else {
    console.warn(`Layer '${props.id}' already exists on the map.`)
  }

  setTimeout(() => {
    emit('mounted')
  }, 1000)
}

const removeLayer = () => {
  if (props.map && props.map.getLayer && props.map.getLayer(props.id)) {
    if (props.image) {
      props.map.removeImage(props.id)
    }
    props.map.removeLayer(props.id)
    removeLayerListeners(props.id)
  }
}

onMounted(async () => {
  await addLayer()
  props.map.on('style.load', addLayer)
})

onUnmounted(() => {
  removeLayer()
  props.map.off('style.load', addLayer)
})
</script>

<template>
  <slot></slot>
</template>
